import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { ArrowLeft, ArrowRight, CheckCircle2, ExternalLink, XCircle, Loader2 } from "lucide-react";
import Button from "../../components/ui/Button";

type OAuthPlatform = "xero" | "procore";

type WizardStep = "select-platform" | "configure" | "result";

type PlatformConfig = {
  id: OAuthPlatform;
  name: string;
  description: string;
  scopes: string[];
};

type ConfigFormData = {
  connectionName: string;
};

export default () => {
  const [searchParams] = useSearchParams();
  const callbackPlatform = searchParams.get("platform") as OAuthPlatform | null;
  const callbackStatus = searchParams.get("status");
  const callbackReason = searchParams.get("reason");
  const callbackCount = searchParams.get("count");

  const hasCallback = !!callbackPlatform && !!callbackStatus;

  const [platforms, setPlatforms] = useState<PlatformConfig[]>([]);
  const [step, setStep] = useState<WizardStep>(hasCallback ? "result" : "select-platform");
  const [selectedPlatform, setSelectedPlatform] = useState<OAuthPlatform | null>(callbackPlatform);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetch("/api/integrations/oauth/config")
      .then(r => r.json())
      .then((data: { platforms: PlatformConfig[] }) => setPlatforms(data.platforms))
      .catch(() => setError("Failed to load platform config"));
  }, []);

  const configForm = useForm<ConfigFormData>({
    defaultValues: {
      connectionName: "",
    },
  });

  const platformConfig = selectedPlatform ? platforms.find(p => p.id === selectedPlatform) : null;

  const handlePlatformSelect = (platform: OAuthPlatform) => {
    setSelectedPlatform(platform);
    setError(null);
    configForm.reset({ connectionName: `${platforms.find(p => p.id === platform)?.name ?? platform} Test` });
    setStep("configure");
  };

  const handleStartOAuth = async (data: ConfigFormData) => {
    if (!selectedPlatform) return;

    setIsRedirecting(true);
    setError(null);

    try {
      const response = await fetch("/api/integrations/oauth/start", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          platform: selectedPlatform,
          connectionName: data.connectionName,
          returnTo: "/oauth-test",
        }),
      });

      const result = await response.json() as { authUrl?: string; error?: string };

      if (!response.ok || result.error) {
        setError(result.error ?? `Failed to start OAuth (${response.status})`);
        setIsRedirecting(false);
        return;
      }

      if (result.authUrl) {
        window.location.href = result.authUrl;
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Network error");
      setIsRedirecting(false);
    }
  };

  const handleReset = () => {
    setStep("select-platform");
    setSelectedPlatform(null);
    setError(null);
    setIsRedirecting(false);
    configForm.reset({ connectionName: "" });
    // Clear URL params without reload
    window.history.replaceState({}, "", "/oauth-test");
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-[var(--theme-bg-base)] p-6">
      <div className="w-full max-w-lg">
        <div className="mb-6 text-center">
          <h1 className="text-xl font-bold text-[var(--theme-text-strong)]">OAuth Integration Wizard</h1>
          <p className="mt-1 text-sm text-[var(--theme-text-subtle)]">Test OAuth flows for supported platforms</p>
        </div>

        {/* Step indicator */}
        <div className="mb-6 flex items-center justify-center gap-2">
          {(["select-platform", "configure", "result"] as WizardStep[]).map((s, i) => {
            const labels = ["Platform", "Configure", "Result"];
            const isActive = s === step;
            const isPast =
              (s === "select-platform" && (step === "configure" || step === "result")) ||
              (s === "configure" && step === "result");

            return (
              <div key={s} className="flex items-center gap-2">
                {i > 0 && <div className={`h-px w-8 ${isPast || isActive ? "bg-[var(--theme-accent)]" : "bg-[var(--theme-inverse-border)]"}`} />}
                <div className="flex items-center gap-1.5">
                  <div
                    className={`flex h-6 w-6 items-center justify-center rounded-full text-xs font-semibold ${
                      isActive
                        ? "bg-[var(--theme-accent)] text-white"
                        : isPast
                          ? "bg-[var(--theme-accent)]/20 text-[var(--theme-accent)]"
                          : "bg-[var(--theme-inverse-border)] text-[var(--theme-text-subtle)]"
                    }`}
                  >
                    {isPast ? "\u2713" : i + 1}
                  </div>
                  <span className={`text-xs font-medium ${isActive ? "text-[var(--theme-text-strong)]" : "text-[var(--theme-text-subtle)]"}`}>
                    {labels[i]}
                  </span>
                </div>
              </div>
            );
          })}
        </div>

        <div className="rounded-2xl border border-[var(--theme-inverse-border)] bg-[var(--theme-inverse-bg-elevated)] p-6">
          {/* Step 1: Select Platform */}
          {step === "select-platform" && (
            <div className="space-y-3">
              <p className="text-sm font-medium text-[var(--theme-text-default)]">Select a platform to connect</p>
              {platforms.map(platform => (
                <button
                  key={platform.id}
                  className="flex w-full items-center justify-between rounded-xl border border-[var(--theme-inverse-border)] bg-[var(--theme-inverse-bg)] px-4 py-3 text-left transition-colors hover:border-[var(--theme-accent)]/50 hover:bg-[var(--theme-inverse-bg)]/80"
                  onClick={() => handlePlatformSelect(platform.id)}
                >
                  <div>
                    <p className="text-sm font-semibold text-[var(--theme-text-strong)]">{platform.name}</p>
                    <p className="mt-0.5 text-xs text-[var(--theme-text-subtle)]">{platform.description}</p>
                  </div>
                  <ArrowRight className="h-4 w-4 shrink-0 text-[var(--theme-text-subtle)]" />
                </button>
              ))}
            </div>
          )}

          {/* Step 2: Configure & Start */}
          {step === "configure" && platformConfig && (
            <form onSubmit={configForm.handleSubmit(handleStartOAuth)} className="space-y-4">
              <div className="flex items-center gap-2">
                <button type="button" onClick={() => setStep("select-platform")} className="text-[var(--theme-text-subtle)] hover:text-[var(--theme-text-default)]">
                  <ArrowLeft className="h-4 w-4" />
                </button>
                <p className="text-sm font-medium text-[var(--theme-text-default)]">
                  Connect to {platformConfig.name}
                </p>
              </div>

              <div className="space-y-3">
                <div>
                  <label className="mb-1 block text-xs font-medium text-[var(--theme-text-muted)]">Connection Name</label>
                  <input
                    {...configForm.register("connectionName", { required: true })}
                    className="h-9 w-full rounded-lg border border-[var(--theme-inverse-border)] bg-[var(--theme-inverse-bg)] px-3 text-sm text-[var(--theme-text-strong)] outline-none focus:border-[var(--theme-accent)]"
                    placeholder="My Xero Account"
                  />
                </div>

                <div>
                  <label className="mb-1 block text-xs font-medium text-[var(--theme-text-muted)]">Scopes</label>
                  <div className="flex flex-wrap gap-1.5 rounded-lg border border-[var(--theme-inverse-border)] bg-[var(--theme-inverse-bg)] p-2.5">
                    {platformConfig.scopes.map(scope => (
                      <span key={scope} className="rounded-md bg-[var(--theme-bg-panel)] px-2 py-0.5 text-[11px] font-medium text-[var(--theme-text-muted)]">
                        {scope}
                      </span>
                    ))}
                  </div>
                  <p className="mt-1 text-[11px] text-[var(--theme-text-subtle)]">Loaded from environment variables</p>
                </div>
              </div>

              {error && (
                <div className="flex items-start gap-2 rounded-lg border border-rose-500/30 bg-rose-500/10 px-3 py-2">
                  <XCircle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-rose-400" />
                  <p className="text-xs text-rose-200">{error}</p>
                </div>
              )}

              <Button type="submit" className="w-full" disabled={isRedirecting}>
                {isRedirecting ? (
                  <span className="flex items-center justify-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Redirecting to {platformConfig.name}...
                  </span>
                ) : (
                  <span className="flex items-center justify-center gap-2">
                    <ExternalLink className="h-4 w-4" />
                    Authorize with {platformConfig.name}
                  </span>
                )}
              </Button>
            </form>
          )}

          {/* Step 3: Result */}
          {step === "result" && (
            <div className="space-y-4">
              {callbackStatus === "connected" ? (
                <div className="text-center">
                  <CheckCircle2 className="mx-auto h-10 w-10 text-emerald-400" />
                  <p className="mt-3 text-sm font-semibold text-[var(--theme-text-strong)]">
                    {platformConfig?.name ?? callbackPlatform} Connected
                  </p>
                  <p className="mt-1 text-xs text-[var(--theme-text-subtle)]">
                    OAuth flow completed successfully.
                    {callbackCount && ` ${callbackCount} tenant${callbackCount === "1" ? "" : "s"} linked.`}
                  </p>
                  <p className="mt-2 text-xs text-[var(--theme-text-subtle)]">
                    Tokens have been stored in MongoDB. The connection is ready to use.
                  </p>
                </div>
              ) : (
                <div className="text-center">
                  <XCircle className="mx-auto h-10 w-10 text-rose-400" />
                  <p className="mt-3 text-sm font-semibold text-[var(--theme-text-strong)]">
                    Connection Failed
                  </p>
                  <p className="mt-1 text-xs text-rose-300">
                    {callbackReason ?? "Unknown error during OAuth callback"}
                  </p>
                </div>
              )}

              <div className="flex gap-2">
                <Button variant="ghost" className="flex-1" onClick={handleReset}>
                  <span className="flex items-center justify-center gap-2">
                    <ArrowLeft className="h-4 w-4" />
                    Start Over
                  </span>
                </Button>
                {callbackStatus === "connected" && (
                  <Button className="flex-1" onClick={() => window.location.href = "/dashboard/integrations"}>
                    View Connections
                  </Button>
                )}
              </div>
            </div>
          )}
        </div>

        <p className="mt-4 text-center text-[11px] text-[var(--theme-text-subtle)]">
          Standalone test page &middot; Uses /api/integrations/oauth/start
        </p>
      </div>
    </div>
  );
};
